import { ApiServiceError } from '@classic/Foundation/Services/ApiService'
import { MFAAttributeName, MFAAttributeNameMap, MfaError, MFAMethod } from '@mobi/api-types/src/mfa'

/**
 * Maps MFA preference values to API attribute names
 */

const attributeMap: MFAAttributeNameMap = {
  SMS: 'phone_number',
  Email: 'email',
  None: 'none',
  SoftwareToken: 'software_token_mfa_code',
  Unknown: 'unknown',
}

export const mapMFAMethodToAttributeName = (method: MFAMethod): MFAAttributeName => {
  return attributeMap[method] as MFAAttributeName
}

/**
 * Returns a human-readable display name for an MFA method
 */
export const getMFADisplayName = (method: MFAMethod): string => {
  switch (method) {
    case 'SMS':
      return 'phone'
    case 'Email':
      return 'email'
    default:
      return method.toLowerCase() // Return the method itself as fallback
  }
}
export async function convertToStandardError(error: unknown): Promise<MfaError> {
  if (error instanceof ApiServiceError) {
    const response = error.response

    try {
      const errorData = response.status !== 204 ? await response.json() : {}

      return {
        status: response.status,
        error: errorData.error || 'UnknownError',
        message: errorData.message || `Request failed (${response.status})`,
      }
    } catch (jsonError) {
      return {
        status: response.status,
        error: `InvalidResponse: ${jsonError} `,
        message: `Request failed (${response.status})`,
      }
    }
  } else if (error instanceof Error) {
    return {
      status: 500,
      error: error.name,
      message: error.message,
    }
  } else {
    return {
      status: 500,
      error: 'UnknownError',
      message: 'An unknown error occurred',
    }
  }
}

export async function throwApiError(error: unknown): Promise<never> {
  const standardError = await convertToStandardError(error)
  throw standardError
}

export const hideEmailPrefix = (email: string | undefined) => {
  if (email === undefined) {
    return 'No contact provided'
  }
  const domain = email.split('@')[1]
  const maskedUsername = '*'.repeat(4)
  return `${maskedUsername}@${domain}`
}

export const hidePhonePrefix = (phone: string | undefined) => {
  if (phone === undefined) {
    return 'No contact provided'
  }
  const lastThree = phone.slice(-3)
  const maskLength = phone.length - 7
  const maskedMiddle = '*'.repeat(maskLength)

  if (phone.startsWith('+614')) {
    return '04' + maskedMiddle + lastThree
  }

  const prefix = phone.substring(0, 4)
  return prefix + maskedMiddle + lastThree
}

export function calculateTimeLeft(expiryTime: number) {
  const difference = expiryTime - Date.now()
  return Math.max(0, Math.floor(difference / 1000))
}

export const formatTime = (seconds: number) => {
  const ts = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0')
  const tm = Math.floor(seconds / 60)
  return `${tm}:${ts}`
}
